import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore
} from '@mui/icons-material';
import { get } from '../../services/apiService';
import MonthlyAutomationStatusChart from '../charts/MonthlyAutomationStatusChart';
import TestFunctionAutomationPivotTable from '../charts/TestFunctionAutomationPivotTable';
import TestDataTable from '../charts/TestDataTable';

/**
 * Tests component for public dashboard
 * Displays test collection statistics
 * @returns {JSX.Element} Tests component
 */
const Tests = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await get('/api/test/public/stats');
        
        if (response.success) {
          setStats(response.data);
        } else {
          throw new Error(response.message || 'Failed to fetch statistics');
        }
      } catch (err) {
        console.error('Error fetching test statistics:', err);
        setError('Failed to load test statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Loading test statistics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert severity="info">
        No test data available.
      </Alert>
    );
  }

  const totalTests = stats.totalTests || 0;
  const automatedTests = stats.automatedTests || {};
  const automatedTotal = automatedTests.total || 0;
  const breakdown = automatedTests.breakdown || {};
  const toBeAutomated = stats.toBeAutomated || 0;
  const tbd = stats.tbd ?? 0;
  const manual = stats.manual || 0;
  const other = stats.other ?? 0;
  const suiteBreakdown = stats.suiteBreakdown || [];
  // Use breakdown sum when provided so Total Tests always matches sum of status cards
  const sumOfStatuses = automatedTotal + toBeAutomated + tbd + manual + other;
  const displayTotal = (stats.breakdownSum != null ? stats.breakdownSum : totalTests) || sumOfStatuses;

  // Generate colors for test suite cards
  const generatePieColors = (count) => {
    const colors = [
      '#1A2E22', '#2C4A3A', '#4caf50', '#2196f3', '#ff9800',
      '#f44336', '#9c27b0', '#00bcd4', '#ffeb3b', '#795548',
      '#607d8b', '#e91e63', '#009688', '#3f51b5', '#ff5722'
    ];
    return colors.slice(0, count);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
          Tests Overview
        </Typography>
      </Box>

      {/* Statistics Cards - Main row with Total Tests and other metrics */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2, 
          mb: 3,
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Grid container spacing={2} sx={{ alignItems: 'stretch', flexGrow: 1 }}>
          {/* Left Column: Total Tests + Test Suite Cards */}
          <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Block 1: Total Tests */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #1A2E22 0%, #2C4A3A 100%)',
                color: 'white',
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>
                Total Tests
              </Typography>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 'bold',
                  mb: 0.5
                }}
              >
                {displayTotal.toLocaleString()}
              </Typography>
            </Paper>

            {/* Test Suite Cards - Vertical */}
            {suiteBreakdown.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                  Tests by Test Suite
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1.5, 
                  flexGrow: 1,
                  justifyContent: 'flex-start'
                }}>
                  {suiteBreakdown.map((item, index) => {
                    const suiteName = item.testSuite || 'Not Specified';
                    const count = item.count || 0;
                    const total = suiteBreakdown.reduce((sum, s) => sum + (s.count || 0), 0);
                    const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                    const color = generatePieColors(suiteBreakdown.length)[index];
                    
                    return (
                      <Paper 
                        key={index}
                        elevation={1} 
                        sx={{ 
                          p: 1.5, 
                          textAlign: 'center',
                          borderLeft: `4px solid ${color}`,
                          backgroundColor: 'background.default',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: color,
                            mb: 0.5
                          }}
                        >
                          {count.toLocaleString()}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ mb: 0.5, fontSize: '0.75rem' }}
                        >
                          {suiteName}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: color,
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }}
                        >
                          {percentage}%
                        </Typography>
                      </Paper>
                    );
                  })}
                  {/* Spacer to fill remaining space and align with chart bottom */}
                  <Box sx={{ flexGrow: 1 }} />
                </Box>
              </Box>
            )}
          </Grid>

          {/* Right Column: Other Metrics Cards + Chart */}
          <Grid item xs={12} md={9} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box 
              sx={{ 
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                flexWrap: { xs: 'nowrap', md: 'nowrap' },
                flexShrink: 0
              }}
            >
              {/* Block 2: Automated Tests Total */}
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  borderLeft: '4px solid #4caf50',
                  backgroundColor: 'background.default',
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#4caf50',
                    mb: 0.5
                  }}
                >
                  {automatedTotal.toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Automated Tests
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#4caf50',
                    fontWeight: 500
                  }}
                >
                  {displayTotal > 0 ? ((automatedTotal / displayTotal) * 100).toFixed(1) : 0}%
                </Typography>
              </Paper>

              {/* Block 3: Automated-UI */}
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  borderLeft: '4px solid #2196f3',
                  backgroundColor: 'background.default',
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#2196f3',
                    mb: 0.5
                  }}
                >
                  {(breakdown['Automated-UI'] || 0).toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Automated-UI
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#2196f3',
                    fontWeight: 500
                  }}
                >
                  {automatedTotal > 0 ? ((breakdown['Automated-UI'] || 0) / automatedTotal * 100).toFixed(1) : 0}%
                </Typography>
              </Paper>

              {/* Block 4: Automated-API */}
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  borderLeft: '4px solid #ff9800',
                  backgroundColor: 'background.default',
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#ff9800',
                    mb: 0.5
                  }}
                >
                  {(breakdown['Automated-API'] || 0).toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Automated-API
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#ff9800',
                    fontWeight: 500
                  }}
                >
                  {automatedTotal > 0 ? ((breakdown['Automated-API'] || 0) / automatedTotal * 100).toFixed(1) : 0}%
                </Typography>
              </Paper>

              {/* Block 5: EPAM-Automated */}
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  borderLeft: '4px solid #9c27b0',
                  backgroundColor: 'background.default',
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#9c27b0',
                    mb: 0.5
                  }}
                >
                  {(breakdown['EPAM-Automated'] || 0).toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  EPAM-Automated
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#9c27b0',
                    fontWeight: 500
                  }}
                >
                  {automatedTotal > 0 ? ((breakdown['EPAM-Automated'] || 0) / automatedTotal * 100).toFixed(1) : 0}%
                </Typography>
              </Paper>

              {/* Block 7: To be Automated */}
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  borderLeft: '4px solid #ff9800',
                  backgroundColor: 'background.default',
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#ff9800',
                    mb: 0.5
                  }}
                >
                  {toBeAutomated.toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  To be Automated
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#ff9800',
                    fontWeight: 500
                  }}
                >
                  {displayTotal > 0 ? ((toBeAutomated / displayTotal) * 100).toFixed(1) : 0}%
                </Typography>
              </Paper>

              {/* TBD */}
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  borderLeft: '4px solid #9e9e9e',
                  backgroundColor: 'background.default',
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: '#757575',
                    mb: 0.5
                  }}
                >
                  {tbd.toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  TBD
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#757575',
                    fontWeight: 500
                  }}
                >
                  {displayTotal > 0 ? ((tbd / displayTotal) * 100).toFixed(1) : 0}%
                </Typography>
              </Paper>

              {/* Block 8: Manual */}
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  borderLeft: '4px solid #9e9e9e',
                  backgroundColor: 'background.default',
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#9e9e9e',
                    mb: 0.5
                  }}
                >
                  {manual.toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Manual
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#9e9e9e',
                    fontWeight: 500
                  }}
                >
                  {displayTotal > 0 ? ((manual / displayTotal) * 100).toFixed(1) : 0}%
                </Typography>
              </Paper>

              {/* Other statuses (so breakdown sums to Total Tests) */}
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  borderLeft: '4px solid #607d8b',
                  backgroundColor: 'background.default',
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: '#607d8b',
                    mb: 0.5
                  }}
                >
                  {other.toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Other statuses
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#607d8b',
                    fontWeight: 500
                  }}
                >
                  {displayTotal > 0 ? ((other / displayTotal) * 100).toFixed(1) : 0}%
                </Typography>
              </Paper>
            </Box>

            {/* Monthly Automation Status Chart */}
            <Box sx={{ mt: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <MonthlyAutomationStatusChart isPublic={true} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Function vs Automation Status Pivot Table - Expandable */}
      <Accordion defaultExpanded={false} sx={{ mt: 3 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="pivot-table-content"
          id="pivot-table-header"
          sx={{
            backgroundColor: '#1976d2',
            color: 'white',
            '&:hover': {
              backgroundColor: '#1565c0'
            },
            '& .MuiAccordionSummary-content': {
              alignItems: 'center'
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Function vs Automation Status Pivot Table
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <TestFunctionAutomationPivotTable />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Test Data Table - Expandable (Collapsed by default) */}
      <Accordion defaultExpanded={false} sx={{ mt: 3 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="data-table-content"
          id="data-table-header"
          sx={{
            backgroundColor: '#1976d2',
            color: 'white',
            '&:hover': {
              backgroundColor: '#1565c0'
            },
            '& .MuiAccordionSummary-content': {
              alignItems: 'center'
            }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Test Data Table
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <TestDataTable />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default Tests;

